import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useBlocker } from "react-router-dom";
import type { AnyBlock, Block, BlockDocumentJson, BlockType, LineItem } from "@/types";
import { QuoteRenderer, type QuoteContext } from "@/renderer";
import { Button } from "@/components/ui";
import { createBlock } from "./defaults";
import { AddBlockMenu } from "./AddBlockMenu";
import { BlockRow } from "./BlockRow";
import { buildWarnings } from "./guardrails";
import "./builder.css";

/**
 * The presentational two-pane content builder (§4/§7) — shared by the quote and
 * template editors. It owns the in-memory draft, dirty tracking, add/remove/
 * reorder, soft guardrails, the live preview, and Save/Discard. It does NO data
 * fetching: the parent loads `initialContent` and supplies `onSave`. Preview
 * always renders through the one shared <QuoteRenderer mode="preview">.
 */
export function ContentBuilder({
  heading,
  initialContent,
  lineItems,
  quoteContext,
  onSave,
  onBack,
  warningSubject = "quote",
}: {
  heading: ReactNode;
  initialContent: BlockDocumentJson;
  lineItems: LineItem[];
  quoteContext?: QuoteContext;
  onSave: (content: BlockDocumentJson) => Promise<void>;
  onBack: () => void;
  warningSubject?: "quote" | "template";
}) {
  const [draft, setDraft] = useState<BlockDocumentJson>(initialContent);
  const [baseline, setBaseline] = useState<string>(JSON.stringify(initialContent));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const dirty = JSON.stringify(draft) !== baseline;

  const warnings = useMemo(() => buildWarnings(draft.blocks, warningSubject), [draft.blocks, warningSubject]);

  // (1) Block ALL in-app navigation away from this route while dirty — back
  // link, header menu links, browser back/forward — via the router blocker.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm("You have unsaved changes. Leave without saving?")) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  // Keep beforeunload for the cases the router can't see: refresh, tab close,
  // and navigation to external URLs.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const setBlocks = (blocks: AnyBlock[]) => setDraft((d) => ({ ...d, blocks }));
  const addBlock = (type: BlockType) => setBlocks([...draft.blocks, createBlock(type)]);
  const updateBlock = (block: Block) =>
    setBlocks(draft.blocks.map((b) => (b.id === block.id ? block : b)));
  const removeBlock = (blockId: string) => setBlocks(draft.blocks.filter((b) => b.id !== blockId));
  const moveBlock = (index: number, dir: -1 | 1) => {
    const next = [...draft.blocks];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBlocks(next);
  };

  async function save() {
    // (2) Advisory guardrail confirm at save time — same checks as the inline
    // banner, so the two never disagree. Never blocks the save outright.
    if (warnings.length > 0) {
      const message = `${warnings.join("\n\n")}\n\nSave anyway?`;
      if (!window.confirm(message)) return;
    }
    setSaving(true);
    setError(undefined);
    try {
      await onSave(draft);
      setBaseline(JSON.stringify(draft)); // new clean baseline
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    setDraft(JSON.parse(baseline) as BlockDocumentJson);
  }

  // The router blocker owns the unsaved-changes confirm; just navigate here.
  function back() {
    onBack();
  }

  return (
    <div className="builder">
      <header className="builder__bar">
        <div className="builder__bar-left">
          <button type="button" className="builder__back" onClick={back}>← Back</button>
          <h1>{heading}</h1>
        </div>
        <div className="builder__bar-actions">
          {dirty ? <span className="builder__dirty">Unsaved changes</span> : null}
          <Button variant="ghost" onClick={discard} disabled={!dirty || saving}>Discard</Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={!dirty}>Save</Button>
        </div>
      </header>

      {error ? <p className="builder__error" role="alert">{error}</p> : null}

      <div className="builder__panes">
        <section className="builder__editor" aria-label="Block editor">
          <AddBlockMenu onAdd={addBlock} />

          {warnings.length > 0 ? (
            <ul className="builder__warnings">
              {warnings.map((w, i) => (
                <li key={i} className="builder__warning">{w}</li>
              ))}
            </ul>
          ) : null}

          {draft.blocks.length === 0 ? (
            <p className="builder__empty">No blocks yet. Add one to get started.</p>
          ) : (
            <ol className="builder__list">
              {draft.blocks.map((block, i) => (
                <BlockRow
                  key={block.id}
                  block={block}
                  index={i}
                  count={draft.blocks.length}
                  onChange={updateBlock}
                  onRemove={() => removeBlock(block.id)}
                  onMove={(dir) => moveBlock(i, dir)}
                />
              ))}
            </ol>
          )}
        </section>

        <section className="builder__preview" aria-label="Live preview">
          <div className="builder__preview-frame">
            <QuoteRenderer
              mode="preview"
              blocks={draft.blocks}
              lineItems={lineItems}
              quote={quoteContext}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
